/**
 * ============================================================================
 *                        SIDEBAR ICON MAPPINGS
 *                    Developer Guide & Configuration
 * ============================================================================
 * 
 * This file maps menu icons to Material UI icon components.
 * 
 * QUICK START - Adding a new icon:
 * ---------------------------------
 * 1. Import the MUI icon at the top of this file
 * 2. Add a mapping entry in `iconMapping` object
 * 
 * Example: Adding a "camera" icon:
 *   Step 1: import CameraAltIcon from '@mui/icons-material/CameraAlt';
 *   Step 2: 'fa-camera': CameraAltIcon,
 * 
 * FINDING MUI ICONS:
 * Browse all icons at: https://mui.com/material-ui/material-icons/
 * 
 * ============================================================================
 */

import React from 'react';

// ============================================================================
// MATERIAL UI ICON IMPORTS
// ============================================================================
// Add new icon imports here as needed
// Browse icons: https://mui.com/material-ui/material-icons/

// Core / Navigation
import DashboardIcon from '@mui/icons-material/Dashboard';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FolderIcon from '@mui/icons-material/Folder';

// Documents / Files
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';

// Media / Content
import MovieIcon from '@mui/icons-material/Movie';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import TvIcon from '@mui/icons-material/Tv';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ImageIcon from '@mui/icons-material/Image';

// Organization / Structure
import LayersIcon from '@mui/icons-material/Layers';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

// Calendar / Time
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventNoteIcon from '@mui/icons-material/EventNote';
import TimelineIcon from '@mui/icons-material/Timeline';

// Actions
import PublishIcon from '@mui/icons-material/Publish';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ShareIcon from '@mui/icons-material/Share';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Charts / Analytics
import BarChartIcon from '@mui/icons-material/BarChart';
import AssessmentIcon from '@mui/icons-material/Assessment';

// Business
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InventoryIcon from '@mui/icons-material/Inventory';

// System / Settings
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import GroupIcon from '@mui/icons-material/Group';

// Layout
import PagesIcon from '@mui/icons-material/Pages';
import WebIcon from '@mui/icons-material/Web';

// Navigation helpers (for expand/collapse)
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// ============================================================================
// DEFAULT ICON SIZE
// ============================================================================
const ICON_SIZE = 20;

// ============================================================================
// ICON MAPPING
// ============================================================================
// 
// Maps Font Awesome class names to Material UI icons.
// The key is a pattern to match in the FA class (e.g., 'fa-heart' matches 'fa fa-lg fa-fw fa-heart')
// 
// HOW TO ADD A NEW ICON:
// 1. Import the icon from '@mui/icons-material'
// 2. Add an entry: 'fa-your-icon': YourImportedIcon,
// 
// TIP: Use partial matches - 'fa-heart' matches 'fa-heart', 'fa-heart-filled', etc.
// 
const iconMapping = {
    // -- CORE NAVIGATION --
    'fa-tachometer': DashboardIcon,         // Dashboard
    'fa-heart': FavoriteIcon,               // Favorites
    'fa-folder': FolderIcon,                // Default folder (submenu items)

    // -- DOCUMENTS --
    'fa-file-pen': DescriptionIcon,         // Contracts/Documents
    'fa-file-export': UploadFileIcon,       // Export

    // -- MEDIA & CONTENT --
    'fa-clapperboard': MovieIcon,           // Movies/Video content
    'fa-window-restore': OndemandVideoIcon, // VOD
    'fa-tv': TvIcon,                        // TV/Broadcast
    'fa-music': MusicNoteIcon,              // Music
    'fa-image': ImageIcon,                  // Images

    // -- ORGANIZATION --
    'fa-layer-group': LayersIcon,           // Layers/Groups
    'fa-align-center': AccountTreeIcon,     // Tree structure
    'fa-sitemap': WebIcon,                  // Website/Sitemap

    // -- CALENDAR & TIME --
    'fa-calendar-alt': CalendarMonthIcon,   // Planning
    'fa-calendar-day': EventNoteIcon,       // Scheduling
    'fa-timeline': TimelineIcon,            // Timeline

    // -- ACTIONS --
    'fa-file-powerpoint': PublishIcon,      // Publish
    'fa-indent': FileDownloadIcon,          // Download/Ingestion
    'fa-download': SyncAltIcon,             // Sync/Download
    'fa-share-alt': ShareIcon,              // Share/Social
    'fa-plus-square': AddIcon,              // Add new
    'fa-external-link': OpenInNewIcon,      // External link

    // -- ANALYTICS --
    'fa-chart-bar': BarChartIcon,           // Bar charts
    'fa-chart-line': AssessmentIcon,        // Reports

    // -- BUSINESS --
    'fa-th-list': MonetizationOnIcon,       // Monetization
    'fa-thin': ReceiptIcon,                 // Billing  
    'fa-bucket': InventoryIcon,             // Inventory/Bucket
    'fa-pager': PagesIcon,                  // Pages

    // -- SYSTEM --
    'fa-box': SettingsIcon,                 // Settings/Masters
    'fa-bell': NotificationsIcon,           // Notifications
    'fa-servicestack': MiscellaneousServicesIcon, // Services
    'fa-users': GroupIcon,                  // Users
    'fa-audio-description': WebIcon,        // DAI/Ad insertion
};

// ============================================================================
// GET MUI ICON FUNCTION
// ============================================================================
// 
// Returns a Material UI icon component based on Font Awesome class.
// 
// Parameters:
//   faIconClass - Font Awesome icon class string (e.g., 'fa fa-lg fa-fw fa-heart')
//   color       - Icon color (default: 'inherit')
//   size        - Icon size in pixels (default: 20)
// 
// Returns:
//   React component - The matching MUI icon, or FolderIcon if no match
// 
export const getMuiIcon = (faIconClass, color = 'inherit', size = ICON_SIZE) => {
    // No icon specified - return default
    if (!faIconClass) {
        return <FolderIcon sx={{ fontSize: size, color }} />;
    }

    // Find matching icon by pattern
    for (const [pattern, IconComponent] of Object.entries(iconMapping)) {
        if (faIconClass.includes(pattern)) {
            return <IconComponent sx={{ fontSize: size, color }} />;
        }
    }

    // No match found - return default folder icon
    return <FolderIcon sx={{ fontSize: size, color }} />;
};

// ============================================================================
// EXPORTED HELPER ICONS
// ============================================================================
export const ExpandIcon = ExpandMoreIcon;
export const CollapseIcon = ChevronRightIcon;
export const ExternalLinkIcon = OpenInNewIcon;

export default getMuiIcon;
